/**
 * CRM ▸ Workplace ▸ Scheduler — port of the legacy DevExpress SchedulerListEditor
 * (Activity_ListView, caption "Schedular") over Modules.BusinessObjects.Crm.Activity.
 *
 * FULL 6-VIEW PARITY (owner decision 2026-10-26): Day · Work Week · Week · Month ·
 * Timeline · Agenda, matching the legacy toolbar (New · Edit · Edit Series · Delete ·
 * Open Series · Refresh · Restore Occurrence) and the mini date-navigator. Appointment
 * New/Edit/Delete is wired; RECURRING SERIES are deferred — Edit Series / Open Series /
 * Restore Occurrence render disabled with a tooltip until that follow-up lands.
 *
 * No calendar npm dependency (standing rule 8 / §15 — new deps need review): the
 * calendar is hand-rolled, the same approach as the Task Calendar (ScheduledTaskPage)
 * and the Service Scheduling calendar (ServiceCalendar). Data comes from
 * /api/v1/crm-scheduler (dbo.[Event] + dbo.Activity). Dates are server-local naive
 * (ADR-0008): the wire value has no `Z`, so `new Date(appt.startOn)` is the right
 * local instant and the datetime-local inputs round-trip it unchanged.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import { fmtLocalDate } from '../../utils/datetime';
import { navMeta, pageGroups } from '../../layouts/navData';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate } from '../../components/confirm';
import { useModalKeys } from '../../hooks/useModalKeys';

// ─────────────────────────────────────────────────────────────────────────────
// Types (camelCase JSON from CrmAppointmentDto / IcmLookupOption)
// ─────────────────────────────────────────────────────────────────────────────
interface Appt {
  oid:               string;
  subject:           string | null;
  location:          string | null;
  description:       string | null;
  startOn:           string;          // naive local ISO, no Z
  endOn:             string | null;
  allDay:            boolean;
  label:             number;
  status:            number;
  type:              number;
  ownerId:           string | null;
  ownerName:         string | null;
  isRecurring:       boolean;
  recurrenceInfoXml: string | null;
}
interface Owner { id: string; name: string; }

type ViewMode = 'day' | 'workweek' | 'week' | 'month' | 'timeline' | 'agenda';
const VIEWS: { v: ViewMode; label: string }[] = [
  { v: 'day', label: 'Day' }, { v: 'workweek', label: 'Work Week' },
  { v: 'week', label: 'Week' }, { v: 'month', label: 'Month' },
  { v: 'timeline', label: 'Timeline' }, { v: 'agenda', label: 'Agenda' },
];

// DevExpress Event.Label (colour category 0–10) + Status (availability 0–4).
// token-exempt: appointment category palette — distinct per-category colours (data-viz).
const LABELS: { v: number; name: string; color: string }[] = [
  { v: 0, name: 'None',              color: '#9aa4b2' },
  { v: 1, name: 'Important',         color: '#e5484d' },
  { v: 2, name: 'Business',          color: '#3b82f6' },
  { v: 3, name: 'Personal',          color: '#22c55e' },
  { v: 4, name: 'Vacation',          color: '#a855f7' },
  { v: 5, name: 'Must Attend',       color: '#f59e0b' },
  { v: 6, name: 'Travel Required',   color: '#14b8a6' },
  { v: 7, name: 'Needs Preparation', color: '#06b6d4' },
  { v: 8, name: 'Birthday',          color: '#ec4899' },
  { v: 9, name: 'Anniversary',       color: '#8b5cf6' },
  { v: 10, name: 'Phone Call',       color: '#84cc16' },
];
const STATUSES = ['Free', 'Tentative', 'Busy', 'Out of Office', 'Working Elsewhere'];
const labelColor = (n: number) => (LABELS.find(l => l.v === n) ?? LABELS[0]!).color;

const DOW  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers — built from local parts, never toISOString() (§14b.6: no inline
// date maths that can shift a day).
// ─────────────────────────────────────────────────────────────────────────────
function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfDay(d: Date): Date { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; }
function addDays(d: Date, n: number): Date { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function addMonths(d: Date, n: number): Date { const r = new Date(d); r.setDate(1); r.setMonth(r.getMonth() + n); return r; }
function sundayOf(d: Date): Date { const r = startOfDay(d); r.setDate(r.getDate() - r.getDay()); return r; }
function mondayOf(d: Date): Date { const s = sundayOf(d); return addDays(s, 1); }
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** The naive wire value parsed as a LOCAL instant (no Z → JS parses local). */
function parse(iso: string): Date { return new Date(iso); }
/** datetime-local value "YYYY-MM-DDTHH:mm" from a naive ISO. */
function toInput(iso: string | null): string {
  if (!iso) return '';
  // Already naive local; take the first 16 chars (date + HH:mm).
  return iso.length >= 16 ? iso.slice(0, 16) : iso;
}
/** datetime-local input → wire string (append seconds; stays naive local). */
function fromInput(v: string): string { return v.length === 16 ? `${v}:00` : v; }
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 6-week month grid (Sunday-first). */
function monthGrid(d: Date): Date[] {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const start = sundayOf(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// Overlap layout for the time-grid views: cluster overlapping appointments and
// give each a column so concurrent appointments sit side by side.
function layoutDay(items: Appt[]): Map<string, { col: number; cols: number }> {
  const out = new Map<string, { col: number; cols: number }>();
  const sorted = [...items].sort((a, b) => parse(a.startOn).getTime() - parse(b.startOn).getTime());
  let cluster: Appt[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const lanes: number[] = [];
    for (const it of cluster) {
      const s = parse(it.startOn).getTime();
      const e = (it.endOn ? parse(it.endOn) : parse(it.startOn)).getTime();
      let placed = lanes.findIndex(end => end <= s);
      if (placed < 0) { placed = lanes.length; lanes.push(e); } else lanes[placed] = e;
      out.set(it.oid, { col: placed, cols: 0 });
    }
    for (const it of cluster) out.get(it.oid)!.cols = lanes.length;
    cluster = []; clusterEnd = -Infinity;
  };
  for (const it of sorted) {
    const s = parse(it.startOn).getTime();
    const e = (it.endOn ? parse(it.endOn) : parse(it.startOn)).getTime();
    if (cluster.length && s >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, e);
  }
  flush();
  return out;
}

const ROWH = 44;   // px per hour in the time-grid views
const HOURS = Array.from({ length: 24 }, (_, h) => h);
function hourLabel(h: number): string {
  // 24-hour (§18.6 / standing rule 3) — never AM/PM.
  return `${String(h).padStart(2, '0')}:00`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Appointment New/Edit dialog
// ─────────────────────────────────────────────────────────────────────────────
interface Draft {
  subject: string; start: string; end: string; allDay: boolean;
  location: string; description: string; label: number; status: number; ownerId: string;
}
function AppointmentDialog({ mode, oid, initial, owners, onClose, onSaved }: {
  mode: 'new' | 'edit';
  oid: string | null;
  initial: Draft;
  owners: Owner[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const notify = useNotify();
  const gateReason = useReasonGate();
  const [d, setD] = useState<Draft>(initial);
  const [saving, setSaving] = useState(false);
  const modalKeys = useModalKeys(true, { onClose });
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!d.subject.trim()) { notify.warning("'Subject' must not be empty."); return; }
    if (d.end && d.start && d.end < d.start) { notify.warning('The end date must not be earlier than the start date.'); return; }
    setSaving(true);
    try {
      const body = {
        subject: d.subject.trim(),
        startOn: fromInput(d.start),
        endOn: d.end ? fromInput(d.end) : null,
        allDay: d.allDay,
        location: d.location || null,
        description: d.description || null,
        label: d.label,
        status: d.status,
        ownerId: d.ownerId || null,
      };
      if (mode === 'new') {
        await api.post('/api/v1/crm-scheduler/appointments', body);
      } else if (oid) {
        const reason = await gateReason('Scheduler', 'edit');
        if (reason === null) { setSaving(false); return; }
        await runWithAuditReason(reason, () =>
          api.put(`/api/v1/crm-scheduler/appointments/${oid}`, body));
      }
      notify.success('Saved successfully.');
      onSaved();
    } catch (e: unknown) {
      notify.error(e instanceof Error ? e.message : 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="csc-modal-backdrop" onClick={onClose}>
      <div className="csc-modal" onClick={e => e.stopPropagation()} {...modalKeys}>
        <div className="csc-modal-hdr">
          <i className="ti ti-calendar-event" style={{ marginRight: 6, color: 'var(--brand-accent, var(--ai-orange))' }} />
          <span>{mode === 'new' ? 'New Appointment' : 'Edit Appointment'}</span>
          <button className="csc-modal-close" style={{ marginLeft: 'auto' }} onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="csc-modal-body">
          <label className="csc-fld csc-fld-full">
            <span>Subject<b className="csc-req">*</b></span>
            <input type="text" value={d.subject} maxLength={500} autoFocus
                   onChange={e => set('subject', e.target.value)} />
          </label>
          <label className="csc-fld">
            <span>Start:</span>
            <input type="datetime-local" value={d.start} onChange={e => set('start', e.target.value)} />
          </label>
          <label className="csc-fld">
            <span>End:</span>
            <input type="datetime-local" value={d.end} onChange={e => set('end', e.target.value)} />
          </label>
          <label className="csc-fld">
            <span>All Day:</span>
            <input type="checkbox" className="csc-chkbox" checked={d.allDay} onChange={e => set('allDay', e.target.checked)} />
          </label>
          <label className="csc-fld">
            <span>Owner:</span>
            <select value={d.ownerId} onChange={e => set('ownerId', e.target.value)}>
              <option value="">N/A</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label className="csc-fld">
            <span>Location:</span>
            <input type="text" value={d.location} maxLength={200} onChange={e => set('location', e.target.value)} />
          </label>
          <label className="csc-fld">
            <span>Label:</span>
            <select value={d.label} onChange={e => set('label', Number(e.target.value))}>
              {LABELS.map(l => <option key={l.v} value={l.v}>{l.name}</option>)}
            </select>
          </label>
          <label className="csc-fld">
            <span>Status:</span>
            <select value={d.status} onChange={e => set('status', Number(e.target.value))}>
              {STATUSES.map((s, i) => <option key={i} value={i}>{s}</option>)}
            </select>
          </label>
          <label className="csc-fld csc-fld-full csc-fld--top">
            <span>Description:</span>
            <textarea rows={4} value={d.description} onChange={e => set('description', e.target.value)} />
          </label>
        </div>
        <div className="csc-modal-footer">
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini date-navigator (right rail) — legacy's month calendar.
// ─────────────────────────────────────────────────────────────────────────────
function MiniMonth({ month, anchor, onPick, apptDays, header }: {
  month: Date; anchor: Date; onPick: (d: Date) => void; apptDays: Set<string>; header: ReactNode;
}) {
  const grid = monthGrid(month);
  const today = new Date();
  return (
    <div className="csc-mini-month">
      {header}
      <div className="csc-mini-dow">{DOW.map(d => <span key={d}>{d[0]}</span>)}</div>
      <div className="csc-mini-grid">
        {grid.map((d, i) => {
          const out = d.getMonth() !== month.getMonth();
          const cls = `csc-mini-d${out ? ' out' : ''}${sameDay(d, anchor) ? ' sel' : ''}`
                    + `${sameDay(d, today) ? ' today' : ''}${apptDays.has(toYMD(d)) ? ' has' : ''}`;
          return <button key={i} className={cls} onClick={() => onPick(d)}>{d.getDate()}</button>;
        })}
      </div>
    </div>
  );
}

// TWO consecutive months stacked, legacy DevExpress parity (owner request 2026-10-26).
// The first month carries the prev/next controls; both step together.
function MiniNav({ anchor, onPick, apptDays }: {
  anchor: Date; onPick: (d: Date) => void; apptDays: Set<string>;
}) {
  const [m, setM] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useEffect(() => { setM(new Date(anchor.getFullYear(), anchor.getMonth(), 1)); }, [anchor]);
  const next = addMonths(m, 1);
  return (
    <div className="csc-mini">
      <MiniMonth month={m} anchor={anchor} onPick={onPick} apptDays={apptDays}
        header={
          <div className="csc-mini-hdr">
            <button onClick={() => setM(addMonths(m, -1))} title="Previous month"><i className="ti ti-chevron-left" /></button>
            <span>{MONTHS[m.getMonth()]} {m.getFullYear()}</span>
            <button onClick={() => setM(addMonths(m, 1))} title="Next month"><i className="ti ti-chevron-right" /></button>
          </div>
        } />
      <MiniMonth month={next} anchor={anchor} onPick={onPick} apptDays={apptDays}
        header={<div className="csc-mini-hdr csc-mini-hdr-plain"><span>{MONTHS[next.getMonth()]} {next.getFullYear()}</span></div>} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function CrmSchedulerPage({ pageId = 'crm-scheduler' }: { pageId?: string }) {
  const navTitle = navMeta(pageId)?.label ?? 'Schedular';
  const navGroup = pageGroups[pageId] ?? 'CRM';
  const notify = useNotify();
  const { confirm } = useConfirm();
  const gateReason = useReasonGate();

  const [anchor, setAnchor]   = useState(() => startOfDay(new Date()));
  const [view, setView]       = useState<ViewMode>('month');
  const [appts, setAppts]     = useState<Appt[]>([]);
  const [owners, setOwners]   = useState<Owner[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string>();
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  const [maximized, setMaximized] = useState(false);
  const [zoom, setZoom]       = useState(1);   // time-grid row-height multiplier (the +/- zoom)
  const [dialog, setDialog]   = useState<{ mode: 'new' | 'edit'; oid: string | null; initial: Draft } | null>(null);

  // Visible [start,end] window for the fetch, per view.
  const { winStart, winEnd } = useMemo(() => {
    if (view === 'month') {
      const g = monthGrid(anchor);
      return { winStart: g[0]!, winEnd: g[41]! };
    }
    if (view === 'week')     return { winStart: sundayOf(anchor), winEnd: addDays(sundayOf(anchor), 6) };
    if (view === 'workweek') return { winStart: mondayOf(anchor), winEnd: addDays(mondayOf(anchor), 4) };
    if (view === 'agenda')   return { winStart: startOfDay(anchor), winEnd: addDays(anchor, 30) };
    return { winStart: startOfDay(anchor), winEnd: startOfDay(anchor) }; // day, timeline
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Appt[] }>(
        '/api/v1/crm-scheduler/appointments',
        { params: { start: toYMD(winStart), end: toYMD(winEnd) } });
      if (res.data.success) setAppts(res.data.data);
      else setError('Failed to load appointments.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [winStart, winEnd]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // Owners for the dialog — fetched once.
    void (async () => {
      try {
        const res = await api.get<{ success: boolean; data: Owner[] }>('/api/v1/crm-scheduler/owners');
        if (res.data.success) setOwners(res.data.data);
      } catch { /* dialog still works with the free-text None option */ }
    })();
  }, []);

  // Esc restores a maximized calendar (unless a dialog owns the key).
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dialog) setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized, dialog]);

  const selectedAppt = useMemo(() => appts.find(a => a.oid === selected) ?? null, [appts, selected]);

  // Text search (standard list-search row, §18.6) — filters the loaded appointments
  // by Subject / Location / Owner; every view + Export read the filtered set.
  const visibleAppts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return appts;
    return appts.filter(a =>
      (a.subject ?? '').toLowerCase().includes(q)
      || (a.location ?? '').toLowerCase().includes(q)
      || (a.ownerName ?? '').toLowerCase().includes(q));
  }, [appts, search]);

  const apptDays = useMemo(() => {
    const s = new Set<string>();
    for (const a of visibleAppts) s.add(toYMD(parse(a.startOn)));
    return s;
  }, [visibleAppts]);

  const byDay = useMemo(() => {
    const m = new Map<string, Appt[]>();
    for (const a of visibleAppts) {
      const k = toYMD(parse(a.startOn));
      (m.get(k) ?? m.set(k, []).get(k)!).push(a);
    }
    return m;
  }, [visibleAppts]);

  // ── Navigation ──
  const shift = (n: number) => {
    if (view === 'month') setAnchor(a => addMonths(a, n));
    else if (view === 'week' || view === 'workweek') setAnchor(a => addDays(a, n * 7));
    else setAnchor(a => addDays(a, n)); // day, timeline, agenda
  };
  const headerLabel = (): string => {
    if (view === 'month') return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
    if (view === 'week' || view === 'workweek') {
      const s = view === 'week' ? sundayOf(anchor) : mondayOf(anchor);
      const e = addDays(s, view === 'week' ? 6 : 4);
      return `${fmtLocalDate(toYMD(s))} – ${fmtLocalDate(toYMD(e))}`;
    }
    return `${DOW[anchor.getDay()]} ${fmtLocalDate(toYMD(anchor))}`;
  };

  // ── Navigation strip (DevExpress navigator parity): appointment stepping + zoom ──
  const timeView = view === 'day' || view === 'week' || view === 'workweek';
  const sortedAppts = useMemo(
    () => [...visibleAppts].sort((a, b) => parse(a.startOn).getTime() - parse(b.startOn).getTime()),
    [visibleAppts]);
  const goFirst = () => { const a = sortedAppts[0]; if (a) setAnchor(startOfDay(parse(a.startOn))); };
  const goLast  = () => { const a = sortedAppts[sortedAppts.length - 1]; if (a) setAnchor(startOfDay(parse(a.startOn))); };
  const jumpToAppt = (oid: string) => {
    const a = appts.find(x => x.oid === oid);
    if (a) { setAnchor(startOfDay(parse(a.startOn))); setSelected(oid); }
  };

  // ── CRUD ──
  const openNew = (start?: Date) => {
    const s = start ?? new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 9, 0);
    const e = new Date(s.getTime() + 60 * 60 * 1000);
    setDialog({ mode: 'new', oid: null, initial: {
      subject: '', start: toInput(localIso(s)), end: toInput(localIso(e)),
      allDay: false, location: '', description: '', label: 0, status: 2, ownerId: '',
    } });
  };
  const openEdit = (a: Appt) => {
    setDialog({ mode: 'edit', oid: a.oid, initial: {
      subject: a.subject ?? '', start: toInput(a.startOn), end: toInput(a.endOn),
      allDay: a.allDay, location: a.location ?? '', description: a.description ?? '',
      label: a.label, status: a.status, ownerId: a.ownerId ?? '',
    } });
  };
  const deleteSelected = async () => {
    const a = selectedAppt;
    if (!a) return;
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete the appointment "${a.subject ?? ''}"?` })) return;
    const reason = await gateReason('Scheduler', 'delete');
    if (reason === null) return;
    try {
      await runWithAuditReason(reason, () =>
        api.delete(`/api/v1/crm-scheduler/appointments/${a.oid}`));
      notify.success('Deleted.');
      setSelected(null);
      await load();
    } catch (e: unknown) {
      notify.error(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  // Recurrence actions are deferred — rendered disabled with a tooltip.
  const recurrenceSoon = 'Recurring series — coming soon';
  const recurrenceButtons = (
    <>
      <button className="ca-btn" disabled title={recurrenceSoon}><span className="tb-i">🔁</span> Edit Series</button>
      <button className="ca-btn" disabled title={recurrenceSoon}><span className="tb-i">📂</span> Open Series</button>
      <button className="ca-btn" disabled title={recurrenceSoon}><span className="tb-i">↩</span> Restore Occurrence</button>
    </>
  );

  // Export — the current window's appointments (standing rule 7 "Export to").
  const exportData = useMemo(() => ({
    filename: 'Scheduler',
    headers: ['Subject', 'Start', 'End', 'All Day', 'Location', 'Owner', 'Status'],
    rows: visibleAppts.map(a => [
      a.subject ?? '', a.startOn ?? '', a.endOn ?? '', a.allDay ? 'Yes' : 'No',
      a.location ?? '', a.ownerName ?? '', STATUSES[a.status] ?? String(a.status),
    ]),
  }), [visibleAppts]);

  return (
    <div className={`bl-page csc-sched${maximized ? ' csc-full' : ''}`}>
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>{navGroup}</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{navTitle}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId={pageId} title={navTitle} loadMs={loadMs} loading={loading} auditFormName="Scheduler" />
          </div>
        </div>
        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}
      </div>

      <div className="bl-grid-area" style={{ paddingTop: 0 }}>
        <div className="csc-actionbar" style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onNew={() => openNew()}
            onEdit={() => selectedAppt && openEdit(selectedAppt)} editDisabled={!selectedAppt}
            onDelete={() => void deleteSelected()} deleteDisabled={!selectedAppt}
            beforeRefresh={recurrenceButtons}
            onRefresh={() => void load()} refreshing={loading}
            exportData={exportData}
          />
        </div>

        {/* Search row between the toolbar and the calendar (legacy scheduler has a
            Search box; owner request 2026-10-26) — filters Subject / Location / Owner. */}
        <div className="list-search-row csc-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Text to search…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
          {/* Export + Maximize (zoom) icon cluster at the right of the search row —
              the shared grid header tools (BO UI Standard; owner request 2026-10-26). */}
          <div className="csc-grid-acts">
            <ExportMenu iconOnly triggerClassName="csc-gridact" {...exportData} />
            <button className={`csc-gridact${maximized ? ' on' : ''}`}
                    onClick={() => setMaximized(v => !v)} title={maximized ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${maximized ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <div className="bl-card bl-card-flex" style={{ padding: 0 }}>
          <div className="csc-split">
            <div className="csc-main">
              {/* Calendar toolbar — date nav + Today + the Day/Work Week/Week/Month/
                  Timeline/Agenda view tabs. Rendered INSIDE the grid column (csc-main),
                  so its right-aligned (margin-left:auto) view tabs end at the GRID's
                  right edge instead of stretching across above the mini-calendar — G5
                  parity, owner request 2026-11-02 ("the view filter should sit at the
                  top-right of the grid"). Moving it here also raises the mini-calendar
                  to align at the top, as G5 does. */}
              <div className="csc-toolbar">
                <div className="csc-nav">
                  <button className="csc-nav-btn" onClick={() => shift(-1)} title="Previous"><i className="ti ti-chevron-left" /></button>
                  <button className="csc-nav-btn" onClick={() => shift(1)} title="Next"><i className="ti ti-chevron-right" /></button>
                </div>
                <span className="csc-month-label">{headerLabel()}</span>
                <button className="csc-today-btn" onClick={() => setAnchor(startOfDay(new Date()))}>Today</button>
                <div className="csc-view-btns">
                  {VIEWS.map(({ v, label }) => (
                    <button key={v} className={`csc-view-btn${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation strip (DevExpress navigator parity, owner request 2026-10-26):
                  appointment stepping · zoom +/- (time-grid row height) · appointment combo. */}
              <div className="csc-navstrip">
                <div className="csc-navbtns">
                  <button onClick={goFirst} title="First appointment"><i className="ti ti-chevrons-left" /></button>
                  <button onClick={() => shift(-1)} title="Previous"><i className="ti ti-chevron-left" /></button>
                  <button onClick={() => shift(1)} title="Next"><i className="ti ti-chevron-right" /></button>
                  <button onClick={goLast} title="Last appointment"><i className="ti ti-chevrons-right" /></button>
                  <span className="csc-navsep" />
                  <button onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} disabled={!timeView} title="Zoom in">＋</button>
                  <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} disabled={!timeView} title="Zoom out">－</button>
                </div>
                <select className="csc-navcombo" value={selected ?? ''} onChange={e => jumpToAppt(e.target.value)}>
                  <option value="">{sortedAppts.length ? 'Go to appointment…' : 'No appointments'}</option>
                  {sortedAppts.map(a => (
                    <option key={a.oid} value={a.oid}>
                      {`${fmtLocalDate(toYMD(parse(a.startOn)))}${a.allDay ? '' : ' ' + hhmm(parse(a.startOn))} — ${a.subject ?? ''}`}
                    </option>
                  ))}
                </select>
              </div>
              {loading ? (
                <div className="bl-loading" style={{ margin: 40 }}><LoaderSymbol size={16} /> Loading appointments…</div>
              ) : (
                <>
                  {(view === 'day' || view === 'week' || view === 'workweek')
                    && <TimeGrid view={view} anchor={anchor} byDay={byDay} rowH={ROWH * zoom}
                                 selected={selected} onSelect={setSelected} onEdit={openEdit} onNew={openNew} />}
                  {view === 'month'
                    && <MonthView anchor={anchor} byDay={byDay}
                                  selected={selected} onSelect={setSelected} onEdit={openEdit} onNew={openNew} />}
                  {view === 'timeline'
                    && <TimelineView anchor={anchor} byDay={byDay}
                                     selected={selected} onSelect={setSelected} onEdit={openEdit} />}
                  {view === 'agenda'
                    && <AgendaView appts={visibleAppts} selected={selected} onSelect={setSelected} onEdit={openEdit} />}
                </>
              )}
            </div>
            <MiniNav anchor={anchor} onPick={d => setAnchor(startOfDay(d))} apptDays={apptDays} />
          </div>
        </div>
      </div>

      {dialog && (
        <AppointmentDialog
          mode={dialog.mode} oid={dialog.oid} initial={dialog.initial} owners={owners}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); setSelected(null); void load(); }}
        />
      )}

      <style>{STYLE}</style>
    </div>
  );
}

/** A Date → naive local ISO "YYYY-MM-DDTHH:mm:ss" (the wire format). */
function localIso(d: Date): string {
  return `${toYMD(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Time-grid views (Day / Work Week / Week)
// ─────────────────────────────────────────────────────────────────────────────
function TimeGrid({ view, anchor, byDay, selected, onSelect, onEdit, onNew, rowH = ROWH }: {
  view: ViewMode; anchor: Date; byDay: Map<string, Appt[]>;
  selected: string | null; onSelect: (id: string) => void; onEdit: (a: Appt) => void; onNew: (d: Date) => void;
  rowH?: number;
}) {
  const days = useMemo(() => {
    if (view === 'day') return [startOfDay(anchor)];
    const s = view === 'week' ? sundayOf(anchor) : mondayOf(anchor);
    return Array.from({ length: view === 'week' ? 7 : 5 }, (_, i) => addDays(s, i));
  }, [view, anchor]);
  const today = new Date();

  return (
    <div className="csc-tg">
      <div className="csc-tg-head">
        <div className="csc-tg-gutter" />
        {days.map(d => (
          <div key={toYMD(d)} className={`csc-tg-dh${sameDay(d, today) ? ' today' : ''}`}>
            <span className="csc-tg-dow">{DOW[d.getDay()]}</span>
            <span className="csc-tg-dn">{d.getDate()}</span>
          </div>
        ))}
      </div>
      {/* All-day strip */}
      <div className="csc-tg-allday">
        <div className="csc-tg-gutter csc-tg-allday-lbl">all-day</div>
        {days.map(d => {
          const list = (byDay.get(toYMD(d)) ?? []).filter(a => a.allDay);
          return (
            <div key={toYMD(d)} className="csc-tg-allday-cell" onDoubleClick={() => onNew(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0))}>
              {list.map(a => (
                <ApptChip key={a.oid} a={a} selected={selected === a.oid} onSelect={onSelect} onEdit={onEdit} compact />
              ))}
            </div>
          );
        })}
      </div>
      {/* Scrolling hour grid */}
      <div className="csc-tg-scroll">
        <div className="csc-tg-body" style={{ height: 24 * rowH }}>
          <div className="csc-tg-gutter">
            {HOURS.map(h => <div key={h} className="csc-tg-hr" style={{ height: rowH }}>{hourLabel(h)}</div>)}
          </div>
          {days.map(d => {
            const timed = (byDay.get(toYMD(d)) ?? []).filter(a => !a.allDay);
            const lay = layoutDay(timed);
            return (
              <div key={toYMD(d)} className="csc-tg-col"
                   onDoubleClick={e => {
                     const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                     const mins = Math.max(0, Math.min(23.5, (e.clientY - rect.top) / rowH)) * 60;
                     const hh = Math.floor(mins / 60); const mm = Math.round((mins % 60) / 30) * 30;
                     onNew(new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm === 60 ? 0 : mm));
                   }}>
                {HOURS.map(h => <div key={h} className="csc-tg-slot" style={{ height: rowH }} />)}
                {timed.map(a => {
                  const s = parse(a.startOn); const e = a.endOn ? parse(a.endOn) : new Date(s.getTime() + 30 * 60000);
                  const top = (s.getHours() + s.getMinutes() / 60) * rowH;
                  const height = Math.max(18, ((e.getTime() - s.getTime()) / 3600000) * rowH);
                  const pos = lay.get(a.oid) ?? { col: 0, cols: 1 };
                  const w = 100 / pos.cols;
                  return (
                    <div key={a.oid} className={`csc-ev${selected === a.oid ? ' sel' : ''}`}
                         style={{ top, height, left: `calc(${pos.col * w}% + 2px)`, width: `calc(${w}% - 4px)`,
                                  background: labelColor(a.label) }}
                         title={`${a.subject ?? ''}\n${hhmm(s)}–${hhmm(e)}${a.location ? '\n' + a.location : ''}`}
                         onClick={ev => { ev.stopPropagation(); onSelect(a.oid); }}
                         onDoubleClick={ev => { ev.stopPropagation(); onEdit(a); }}>
                      <span className="csc-ev-t">{hhmm(s)}</span> {a.subject}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Month view
// ─────────────────────────────────────────────────────────────────────────────
function MonthView({ anchor, byDay, selected, onSelect, onEdit, onNew }: {
  anchor: Date; byDay: Map<string, Appt[]>;
  selected: string | null; onSelect: (id: string) => void; onEdit: (a: Appt) => void; onNew: (d: Date) => void;
}) {
  const grid = useMemo(() => monthGrid(anchor), [anchor]);
  const today = new Date();
  return (
    <div className="csc-month">
      <div className="csc-month-hdr">{DOW.map(d => <div key={d}>{d}</div>)}</div>
      <div className="csc-month-grid">
        {grid.map((d, i) => {
          const list = byDay.get(toYMD(d)) ?? [];
          const out = d.getMonth() !== anchor.getMonth();
          return (
            <div key={i} className={`csc-md${out ? ' out' : ''}${sameDay(d, today) ? ' today' : ''}`}
                 onDoubleClick={() => onNew(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0))}>
              <div className="csc-md-n">{d.getDate()}</div>
              <div className="csc-md-b">
                {list.slice(0, 3).map(a => (
                  <ApptChip key={a.oid} a={a} selected={selected === a.oid} onSelect={onSelect} onEdit={onEdit} compact />
                ))}
                {list.length > 3 && <div className="csc-md-more">+{list.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline view — the anchor day across a horizontal hour axis.
// ─────────────────────────────────────────────────────────────────────────────
function TimelineView({ anchor, byDay, selected, onSelect, onEdit }: {
  anchor: Date; byDay: Map<string, Appt[]>;
  selected: string | null; onSelect: (id: string) => void; onEdit: (a: Appt) => void;
}) {
  const list = (byDay.get(toYMD(anchor)) ?? []).filter(a => !a.allDay)
    .sort((a, b) => parse(a.startOn).getTime() - parse(b.startOn).getTime());
  // Stack into lanes so overlapping bars don't cover each other.
  const lanes: Appt[][] = [];
  for (const a of list) {
    const s = parse(a.startOn).getTime();
    let placed = false;
    for (const lane of lanes) {
      const last = lane[lane.length - 1]!;
      const le = (last.endOn ? parse(last.endOn) : parse(last.startOn)).getTime();
      if (le <= s) { lane.push(a); placed = true; break; }
    }
    if (!placed) lanes.push([a]);
  }
  const COLW = 70; // px per hour
  return (
    <div className="csc-tl-scroll">
      <div className="csc-tl" style={{ width: 24 * COLW + 10 }}>
        <div className="csc-tl-axis">
          {HOURS.map(h => <div key={h} style={{ width: COLW }}>{hourLabel(h)}</div>)}
        </div>
        {lanes.length === 0 && <div className="csc-tl-empty">No appointments on this day</div>}
        {lanes.map((lane, li) => (
          <div key={li} className="csc-tl-lane">
            {lane.map(a => {
              const s = parse(a.startOn); const e = a.endOn ? parse(a.endOn) : new Date(s.getTime() + 30 * 60000);
              const left = (s.getHours() + s.getMinutes() / 60) * COLW;
              const width = Math.max(28, ((e.getTime() - s.getTime()) / 3600000) * COLW);
              return (
                <div key={a.oid} className={`csc-tl-bar${selected === a.oid ? ' sel' : ''}`}
                     style={{ left, width, background: labelColor(a.label) }}
                     title={`${a.subject ?? ''}\n${hhmm(s)}–${hhmm(e)}`}
                     onClick={() => onSelect(a.oid)} onDoubleClick={() => onEdit(a)}>
                  {a.subject}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agenda view — a forward list grouped by day (legacy Agenda grid).
// ─────────────────────────────────────────────────────────────────────────────
function AgendaView({ appts, selected, onSelect, onEdit }: {
  appts: Appt[]; selected: string | null; onSelect: (id: string) => void; onEdit: (a: Appt) => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, Appt[]>();
    for (const a of [...appts].sort((x, y) => parse(x.startOn).getTime() - parse(y.startOn).getTime())) {
      const k = toYMD(parse(a.startOn));
      (m.get(k) ?? m.set(k, []).get(k)!).push(a);
    }
    return [...m.entries()];
  }, [appts]);
  if (groups.length === 0) return (
    <div className="bl-empty" style={{ margin: 40 }}><i className="ti ti-calendar-off" /> No appointments</div>
  );
  return (
    <div className="csc-agenda">
      {groups.map(([day, list]) => (
        <div key={day} className="csc-ag-group">
          <div className="csc-ag-date">{fmtLocalDate(day)} <span className="csc-ag-dow">{DOW[parse(day + 'T00:00:00').getDay()]}</span></div>
          {list.map(a => {
            const s = parse(a.startOn); const e = a.endOn ? parse(a.endOn) : s;
            return (
              <div key={a.oid} className={`csc-ag-row${selected === a.oid ? ' sel' : ''}`}
                   onClick={() => onSelect(a.oid)} onDoubleClick={() => onEdit(a)}>
                <span className="csc-ag-dot" style={{ background: labelColor(a.label) }} />
                <span className="csc-ag-time">{a.allDay ? 'All day' : `${hhmm(s)}–${hhmm(e)}`}</span>
                <span className="csc-ag-subj">{a.subject}</span>
                <span className="csc-ag-meta">{[a.location, a.ownerName].filter(Boolean).join(' · ')}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Appointment chip (month / all-day strip)
// ─────────────────────────────────────────────────────────────────────────────
function ApptChip({ a, selected, onSelect, onEdit, compact }: {
  a: Appt; selected: boolean; onSelect: (id: string) => void; onEdit: (a: Appt) => void; compact?: boolean;
}) {
  const s = parse(a.startOn);
  return (
    <div className={`csc-chip${selected ? ' sel' : ''}${compact ? ' compact' : ''}`}
         style={{ background: labelColor(a.label) }}
         title={`${a.subject ?? ''}${a.allDay ? '' : '\n' + hhmm(s)}`}
         onClick={e => { e.stopPropagation(); onSelect(a.oid); }}
         onDoubleClick={e => { e.stopPropagation(); onEdit(a); }}>
      {!a.allDay && <span className="csc-chip-t">{hhmm(s)}</span>}
      <span className="csc-chip-s">{a.subject}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles — structural greys via theme tokens; appointment colours are the
// token-exempt DevExpress Label palette (data-viz).
// ─────────────────────────────────────────────────────────────────────────────
const STYLE = `
.csc-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:10px 16px;border-bottom:1px solid var(--ai-line);background:#fff;flex-shrink:0;}
.csc-nav{display:flex;gap:3px;}
.csc-nav-btn{width:28px;height:28px;border:1px solid var(--ai-line);border-radius:5px;background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;color:var(--ai-muted);}
.csc-nav-btn:hover{background:var(--ai-row-hover);}
.csc-month-label{font-size:15px;font-weight:700;color:var(--ai-ink);min-width:170px;}
.csc-today-btn{padding:4px 12px;border:1px solid var(--ai-line);border-radius:5px;font-size:12px;background:#fff;color:var(--ai-ink);cursor:pointer;font-weight:500;}
.csc-today-btn:hover{background:var(--ai-row-hover);}
.csc-view-btns{display:flex;gap:0;margin-left:auto;}
.csc-view-btn{padding:4px 11px;border:1px solid var(--ai-line);border-left-width:0;background:#fff;font-size:12px;color:var(--ai-muted);cursor:pointer;font-weight:500;}
.csc-view-btn:first-child{border-left-width:1px;border-radius:5px 0 0 5px;}
.csc-view-btn:last-child{border-radius:0 5px 5px 0;}
.csc-view-btn.active{background:var(--brand-accent,var(--ai-orange));color:#fff;border-color:var(--brand-accent,var(--ai-orange));}

/* Search-row Export + Maximize icon cluster (shared grid header tools look). */
/* +4px above / −4px below the search-row controls (owner request 2026-10-27,
   image 1005) — scoped to the Scheduler so no other form's search row moves. */
.csc-search-row{display:flex;align-items:center;margin-top:4px;margin-bottom:-4px;}
.csc-grid-acts{margin-left:auto;display:flex;align-items:center;gap:4px;}
.csc-gridact{width:28px;height:28px;border:1px solid var(--ai-line);border-radius:6px;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;color:var(--ai-muted);}
.csc-gridact:hover{background:var(--ai-row-hover);color:var(--ai-ink);}
.csc-gridact.on{background:var(--brand-accent,var(--ai-orange));color:#fff;border-color:var(--brand-accent,var(--ai-orange));}
.csc-gridact i{font-size:17px;line-height:1;}
/* Maximize — mirror ModuleListView's .mlv-fullgrid / the bespoke .page-fullgrid
   (owner decision 2026-09-29, module.css:2625 / accounting.css:312): NO
   position:fixed overlay — that covers the nav rail AND hides the Restore icon.
   Instead the page chrome (breadcrumb + title) is hidden and the calendar card
   fills the CONTENT area in place; the nav rail and the search row (Export +
   Restore) stay visible. Esc or the icon restores. */
.csc-sched.csc-full .bl-top{display:none;}
/* Hide the page action bar on maximize too (New · Edit · Delete · Refresh ·
   Export to · Audit), like ModuleListView's .mlv-fullgrid hides .mlv-toolbar-row.
   The search row stays, so the Restore icon is still reachable (owner report
   2026-10-27, images 1001/1002). */
.csc-sched.csc-full .csc-actionbar{display:none;}
.csc-sched.csc-full .bl-grid-area{overflow:hidden;}
.csc-sched.csc-full .bl-card.bl-card-flex{flex:1 1 0;min-height:0;}
.csc-sched.csc-full .csc-split{flex:1 1 0;min-height:0;}
.csc-sched.csc-full .csc-main{min-height:0;}
.csc-sched.csc-full .csc-tg-scroll,.csc-sched.csc-full .csc-tl-scroll,.csc-sched.csc-full .csc-agenda{max-height:none;}

/* Navigation strip (DevExpress navigator parity): stepping · zoom · appointment combo. */
.csc-navstrip{display:flex;align-items:center;gap:8px;padding:5px 8px;border-bottom:1px solid var(--ai-line);background:#f7f8fa;flex-shrink:0;}
.csc-navbtns{display:flex;align-items:center;gap:2px;}
.csc-navbtns button{width:26px;height:24px;border:1px solid var(--ai-line);background:#fff;border-radius:4px;cursor:pointer;color:var(--ai-muted);display:inline-flex;align-items:center;justify-content:center;font-size:13px;line-height:1;}
.csc-navbtns button:hover:not(:disabled){background:var(--ai-row-hover);color:var(--ai-ink);}
.csc-navbtns button:disabled{opacity:.4;cursor:default;}
.csc-navsep{width:1px;height:18px;background:var(--ai-line);margin:0 4px;}
.csc-navcombo{flex:1;min-width:0;height:26px;border:1px solid var(--ai-line);border-radius:0;background:#fff;padding:2px 8px;font-size:12px;box-sizing:border-box;}

.csc-split{display:flex;gap:0;align-items:stretch;min-height:420px;}
.csc-main{flex:1;min-width:0;overflow:hidden;display:flex;flex-direction:column;}
.csc-mini{width:236px;flex-shrink:0;border-left:1px solid var(--ai-line);padding:10px;background:#fff;}
/* Each month is an ENCLOSED grid panel (legacy DevExpress navigator parity — owner report
   2026-10-26 image 975: the navigator months read as bordered grids docked into the scheduler,
   not loose day buttons floating in a sidebar). */
.csc-mini-month{border:1px solid var(--ai-line);border-radius:6px;overflow:hidden;}
.csc-mini-hdr{display:flex;align-items:center;justify-content:space-between;font-size:12px;font-weight:700;color:var(--ai-ink);padding:5px 8px;background:#fafbfc;border-bottom:1px solid var(--ai-line);}
.csc-mini-hdr button{border:none;background:none;cursor:pointer;color:var(--ai-muted);width:22px;height:22px;border-radius:4px;}
.csc-mini-hdr button:hover{background:var(--ai-row-hover);}
.csc-mini-dow{display:grid;grid-template-columns:repeat(7,1fr);font-size:10px;color:var(--ai-muted);text-align:center;padding:4px 6px 0;}
.csc-mini-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:1px;padding:2px 6px 6px;}
.csc-mini-month + .csc-mini-month{margin-top:10px;}
.csc-mini-hdr-plain{justify-content:center;}
.csc-mini-d{border:none;background:none;cursor:pointer;font-size:11px;height:24px;border-radius:4px;color:var(--ai-ink);position:relative;}
.csc-mini-d:hover{background:var(--ai-row-hover);}
.csc-mini-d.out{color:var(--ai-muted);opacity:.5;}
.csc-mini-d.today{font-weight:700;color:var(--brand-accent,var(--ai-orange));}
.csc-mini-d.sel{background:var(--brand-accent,var(--ai-orange));color:#fff;}
.csc-mini-d.has::after{content:'';position:absolute;bottom:3px;left:50%;transform:translateX(-50%);width:4px;height:4px;border-radius:50%;background:var(--brand-accent,var(--ai-orange));}
.csc-mini-d.sel.has::after{background:#fff;}

/* Month */
.csc-month{display:flex;flex-direction:column;flex:1;}
.csc-month-hdr{display:grid;grid-template-columns:repeat(7,1fr);background:#fafbfc;border-bottom:1px solid var(--ai-line);}
.csc-month-hdr div{padding:6px 8px;font-size:11px;font-weight:600;color:var(--ai-muted);text-align:center;}
.csc-month-grid{display:grid;grid-template-columns:repeat(7,1fr);grid-auto-rows:1fr;flex:1;}
.csc-md{border-right:1px solid var(--ai-line);border-bottom:1px solid var(--ai-line);min-height:92px;padding:3px;overflow:hidden;cursor:default;}
.csc-md.out{background:#fafbfc;}
.csc-md.out .csc-md-n{color:var(--ai-muted);opacity:.6;}
.csc-md.today .csc-md-n{background:var(--brand-accent,var(--ai-orange));color:#fff;border-radius:50%;}
.csc-md-n{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;font-size:11px;font-weight:600;color:var(--ai-ink);padding:0 4px;}
.csc-md-b{display:flex;flex-direction:column;gap:2px;margin-top:2px;}
.csc-md-more{font-size:10px;color:var(--ai-muted);padding-left:3px;}

/* Chip */
.csc-chip{display:flex;align-items:center;gap:4px;border-radius:3px;padding:1px 5px;color:#fff;font-size:11px;cursor:pointer;overflow:hidden;white-space:nowrap;}
.csc-chip.compact{padding:1px 4px;font-size:10px;}
.csc-chip.sel{outline:2px solid var(--ai-ink);outline-offset:-1px;}
.csc-chip-t{font-weight:700;opacity:.9;font-size:10px;}
.csc-chip-s{overflow:hidden;text-overflow:ellipsis;}

/* Time grid */
.csc-tg{display:flex;flex-direction:column;flex:1;min-height:0;}
.csc-tg-head{display:flex;border-bottom:1px solid var(--ai-line);background:#fafbfc;}
.csc-tg-gutter{width:56px;flex-shrink:0;}
.csc-tg-dh{flex:1;text-align:center;padding:6px 0;border-left:1px solid var(--ai-line);}
.csc-tg-dh.today{background:#fff7f0;}
.csc-tg-dow{font-size:10px;color:var(--ai-muted);display:block;}
.csc-tg-dn{font-size:16px;font-weight:700;color:var(--ai-ink);}
.csc-tg-allday{display:flex;border-bottom:1px solid var(--ai-line);min-height:26px;background:#fff;}
.csc-tg-allday-lbl{font-size:9px;color:var(--ai-muted);text-align:right;padding:4px 6px 0 0;}
.csc-tg-allday-cell{flex:1;border-left:1px solid var(--ai-line);padding:2px;display:flex;flex-direction:column;gap:2px;}
.csc-tg-scroll{flex:1;overflow-y:auto;max-height:560px;}
.csc-tg-body{display:flex;position:relative;}
.csc-tg-hr{font-size:10px;color:var(--ai-muted);text-align:right;padding:0 6px;border-top:1px solid var(--ai-line);box-sizing:border-box;}
.csc-tg-col{flex:1;position:relative;border-left:1px solid var(--ai-line);}
.csc-tg-slot{border-top:1px solid #eef1f4;box-sizing:border-box;}
.csc-ev{position:absolute;border-radius:4px;color:#fff;font-size:11px;padding:1px 5px;overflow:hidden;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.18);}
.csc-ev.sel{outline:2px solid var(--ai-ink);outline-offset:-1px;}
.csc-ev-t{font-weight:700;opacity:.9;}

/* Timeline */
.csc-tl-scroll{overflow:auto;max-height:560px;flex:1;}
.csc-tl{padding:8px 0;}
.csc-tl-axis{display:flex;border-bottom:1px solid var(--ai-line);margin-bottom:6px;}
.csc-tl-axis div{font-size:10px;color:var(--ai-muted);text-align:left;border-left:1px solid #eef1f4;box-sizing:border-box;padding-left:2px;}
.csc-tl-lane{position:relative;height:30px;margin-bottom:4px;}
.csc-tl-bar{position:absolute;top:0;height:26px;border-radius:4px;color:#fff;font-size:11px;line-height:26px;padding:0 6px;overflow:hidden;white-space:nowrap;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.18);}
.csc-tl-bar.sel{outline:2px solid var(--ai-ink);outline-offset:-1px;}
.csc-tl-empty{padding:30px;color:var(--ai-muted);font-size:13px;}

/* Agenda */
.csc-agenda{padding:8px 0;overflow:auto;max-height:580px;flex:1;}
.csc-ag-group{margin-bottom:10px;}
.csc-ag-date{font-size:12px;font-weight:700;color:var(--ai-ink);padding:5px 14px;background:#fafbfc;border-top:1px solid var(--ai-line);border-bottom:1px solid var(--ai-line);}
.csc-ag-dow{font-weight:500;color:var(--ai-muted);margin-left:6px;}
.csc-ag-row{display:flex;align-items:center;gap:10px;padding:6px 14px;cursor:pointer;border-bottom:1px solid #f1f3f7;}
.csc-ag-row:hover{background:var(--ai-row-hover);}
.csc-ag-row.sel{background:#fff7f0;}
.csc-ag-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;}
.csc-ag-time{font-size:11px;color:var(--ai-muted);width:110px;flex-shrink:0;}
.csc-ag-subj{font-size:13px;color:var(--ai-ink);font-weight:500;}
.csc-ag-meta{font-size:11px;color:var(--ai-muted);margin-left:auto;}

/* Dialog */
.csc-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:flex-start;justify-content:center;z-index:1200;padding-top:6vh;}
.csc-modal{background:#fff;border-radius:8px;width:620px;max-width:94vw;max-height:88vh;display:flex;flex-direction:column;box-shadow:0 10px 40px rgba(0,0,0,.25);}
.csc-modal-hdr{display:flex;align-items:center;padding:12px 16px;border-bottom:1px solid var(--ai-line);font-size:14px;font-weight:700;color:var(--ai-ink);}
.csc-modal-close{border:none;background:none;cursor:pointer;color:var(--ai-muted);font-size:16px;}
.csc-modal-body{padding:16px 18px;overflow-y:auto;overflow-x:hidden;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:8px 24px;align-items:start;}
/* AI Standard side-label layout (rule 12): a fixed, LEFT-aligned label column
   immediately left of each field; 26px controls, white/square. minmax(0,1fr) +
   min-width:0 let the controls shrink so the datetime inputs never overflow the
   body (which was forcing a horizontal scrollbar). */
.csc-fld{display:grid;grid-template-columns:82px minmax(0,1fr);align-items:center;column-gap:8px;font-size:12px;color:var(--ai-ink);min-width:0;}
.csc-fld-full{grid-column:1 / -1;}
.csc-fld--top{align-items:start;}
.csc-fld--top>span{padding-top:5px;}
.csc-fld>span{font-weight:600;color:var(--ai-ink);white-space:nowrap;}
.csc-fld input[type=text],.csc-fld input[type=datetime-local],.csc-fld select,.csc-fld textarea{width:100%;min-width:0;height:26px;border:1px solid var(--ai-line);border-radius:0;padding:2px 8px;font-size:13px;background:#fff;box-sizing:border-box;}
.csc-fld textarea{height:auto;padding:6px 8px;resize:vertical;}
.csc-fld input:focus,.csc-fld select:focus,.csc-fld textarea:focus{border-color:var(--brand-accent,var(--ai-orange));box-shadow:0 0 0 2px rgba(232,103,10,.12);outline:none;}
.csc-chkbox{width:15px;height:15px;justify-self:start;}
.csc-req{color:var(--ai-red,#e5484d);margin-left:2px;}
.csc-modal-footer{display:flex;align-items:center;padding:10px 16px;border-top:1px solid var(--ai-line);}
`;
